import { useEffect, useRef, useCallback, useState } from 'react';
import { useMediaQuery } from 'theme/useMediaQuery';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from '@hello-pangea/dnd';
import classNames from 'classnames';

import { handleDragEnd } from '../../utils/dragAndDropUtils';
import { DROPPABLE_IDS, SECTION_TYPES } from '../../constants';
import { useSectionsContext } from '../../context/SectionsContext';

import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import AddCategoryButton from './components/AddCategoryButton';
import { UncategorizedSection } from './components/UncategorizedSection';
import { SectionItem } from '../SectionItem';
import { LoadingSkeleton } from './components/LoadingSkeleton';

export const SectionsContent = () => {
  const isMediumOrLarger = useMediaQuery('(min-width: 768px)');
  const inputRef = useRef<HTMLInputElement>(null);

  // Delete modal state - keeping local as it's UI-only
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<string | null>(null);

  const {
    eventId,
    sections,
    sortedSections,
    hasCustomSections,
    selectedSection,
    sectionNames,
    handleNameChange,
    clearSelectedSection,
    setSelectedSection,
    createSectionMutation,
    deleteMutation,
    updateSectionName,
    deleteSection,
    onReorder,
    tiers,
    isEventLoading
  } = useSectionsContext();

  // Focus management
  useEffect(() => {
    if (selectedSection && inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedSection]);

  // Handle successful section creation
  useEffect(() => {
    if (createSectionMutation.isSuccess && sections?.length > 0) {
      const latestSection = sections.reduce((latest, current) =>
        current.sortIndex > latest.sortIndex ? current : latest
      );
      setSelectedSection(latestSection.id);
      handleNameChange(latestSection.id, latestSection.name);
      createSectionMutation.reset();
    }
  }, [createSectionMutation.isSuccess, sections, setSelectedSection, handleNameChange, createSectionMutation]);

  // Clear state after successful deletion
  useEffect(() => {
    if (deleteMutation.isSuccess) {
      clearSelectedSection();
      deleteMutation.reset();
    }
  }, [deleteMutation.isSuccess, clearSelectedSection, deleteMutation]);

  const handleNameBlur = useCallback(
    (sectionId: string): Promise<void> => 
      updateSectionName(sectionId, sectionNames[sectionId]),
    [updateSectionName, sectionNames]
  );

  const handleDeleteSectionWithConfirmation = useCallback(
    (sectionId: string) => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
      setSectionToDelete(sectionId);
      setDeleteModalOpen(true);
    },
    []
  );

  const cancelEdit = useCallback(() => {
    if (selectedSection) {
      // Reset the section name to original and clear selection
      const originalSection = sections?.find(s => s.id === selectedSection);
      if (originalSection) {
        handleNameChange(selectedSection, originalSection.name);
      }
      clearSelectedSection();
    }
  }, [selectedSection, sections, handleNameChange, clearSelectedSection]);

  const confirmDeleteSection = useCallback(() => {
    if (!sectionToDelete || deleteMutation.variables?.sectionId === sectionToDelete) {
      return;
    }

    if (selectedSection === sectionToDelete) {
      clearSelectedSection();
    }

    deleteSection(sectionToDelete);
    setDeleteModalOpen(false);
    setSectionToDelete(null);
  }, [sectionToDelete, deleteMutation, selectedSection, clearSelectedSection, deleteSection]);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      handleDragEnd({
        result,
        sections: sortedSections,
        tiers,
        onReorder
      });
    },
    [sortedSections, tiers, onReorder]
  );

  // Show loading skeleton when event is loading
  if (isEventLoading) {
    return <LoadingSkeleton count={1} showUncategorized={false} />;
  }

  return (
    <div>
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onConfirm={confirmDeleteSection}
        onCancel={() => {
          setDeleteModalOpen(false);
          setSectionToDelete(null);
          clearSelectedSection();
        }}
        isDeleting={deleteMutation.isLoading}
      />

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={DROPPABLE_IDS.SECTIONS} type={SECTION_TYPES.SECTIONS}>
          {(providedSections, snapshotSections) => (
            <div
              className="w-full"
              ref={providedSections.innerRef}
              {...providedSections.droppableProps}
            >
              <div className="grid grid-flow-row md:grid-flow-col-dense md:auto-cols-fr">
                <UncategorizedSection />

                <div
                  className={classNames(
                    'w-full order-last md:order-first transition-colors duration-200 rounded-2xl border-2 border-dashed border-transparent ease-in',
                    {
                      hidden: !hasCustomSections,
                      'bg-[#EFF2FF] !border-[#6585FF]': snapshotSections.isDraggingOver
                    }
                  )}
                >
                  <div className="border-t-2 border-[#EBEBEB] -mx-4 md:mx-6" />
                  {sortedSections?.map((section, index) => (
                    <Draggable
                      index={index}
                      key={section.id}
                      draggableId={section.id}
                    >
                      {(provided, snapshot) => (
                        <SectionItem
                          ref={provided.innerRef}
                          section={section}
                          provided={provided}
                          snapshot={snapshot}
                          snapshotSections={snapshotSections}
                          onNameBlur={handleNameBlur}
                          onDeleteSection={handleDeleteSectionWithConfirmation}
                          onCancelEdit={cancelEdit}
                          inputRef={inputRef}
                        />
                      )}
                    </Draggable>
                  ))}

                  {isMediumOrLarger && !snapshotSections.isDraggingOver && (
                    <div className="flex justify-center mt-4 pl-8">
                      <AddCategoryButton eventId={eventId} />
                    </div>
                  )}

                  {providedSections.placeholder}
                </div>
              </div>
            </div>
          )}
        </Droppable>

        {(!isMediumOrLarger || (isMediumOrLarger && !hasCustomSections)) && (
          <div className="flex flex-col items-center justify-center mb-24">
            <AddCategoryButton eventId={eventId} />
          </div>
        )}
      </DragDropContext>
    </div>
  );
}; 